import { graphqlPost } from "api/graphqlClient";
import { DashboardConfig } from "containers/Dashboard/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { getSearchObj } from "utils/utils";
import { containerSettingsBus } from "./containerSettingsBus";

/**
 * A container's configured dashboard (one `container_dashboard` association row,
 * with its referenced DASHBOARD record expanded).
 */
export interface ContainerDashboardAssoc {
    id: string;
    dashboard_id: string;
    is_default: boolean;
    dashboard_id_dashboard?: {
        id: string;
        name: string;
        icon?: string | null;
        value: DashboardConfig | null;
    } | null;
}

/**
 * Sentinel tab uid for the container's built-in "Default dashboard" view (the
 * Usage & activity panel + resource tabs). Dashboard ids are UUIDs, so this
 * literal can never collide with an additional dashboard's tab uid.
 */
export const CONTAINER_DASHBOARD_DEFAULT_TAB = "__default_dashboard__";

export interface UseContainerDashboardsResult {
    /** Usable associations (those whose referenced dashboard still resolves), or
     *  null while the first fetch is in flight. */
    assocs: ContainerDashboardAssoc[] | null;
    /** Whether the container has at least one additional dashboard configured. */
    hasExtraDashboards: boolean;
    /** Whether the built-in "Default dashboard" tab is the active one (also true
     *  when there are no additional dashboards, i.e. the tab strip is hidden). */
    isDefaultTab: boolean;
    activeTab: string;
    setActiveTab: (uid: string) => void;
    /** The association whose dashboard is currently selected, or undefined when
     *  the Default tab is active. */
    activeAssoc: ContainerDashboardAssoc | undefined;
}

/**
 * Fetches a container's configured dashboards (the `container_dashboard` join
 * rows for WORKSPACE / PROJECT / BUNDLE) and owns the active-tab selection for
 * the container-screen dashboard tab strip (see {@link ContainerDashboardTabs}).
 *
 * Kept as a hook (rather than internal component state) because the active tab
 * gates sibling layout partials on the container screen — when an additional
 * dashboard tab is active the built-in Usage & activity panel and resource tabs
 * are hidden and only that dashboard renders. ResourcesSingle therefore needs
 * the selection state to decide what to render.
 *
 * Data is fetched with a raw GraphQL query string (not a generated typed query)
 * because the committed `schema.graphql` SDL does not yet include the
 * `container_dashboard` type — the deployed backend does. Passing `recordId`
 * undefined (e.g. for non-container record types) makes this a no-op.
 */
export function useContainerDashboards(recordId?: string): UseContainerDashboardsResult {
    const [assocs, setAssocs] = useState<ContainerDashboardAssoc[] | null>(null);
    const [activeTab, setActiveTab] = useState<string>(CONTAINER_DASHBOARD_DEFAULT_TAB);
    // The container id we have already applied the one-time default selection for
    // (so a live association change does not re-yank the active tab).
    const initedRef = useRef<string | undefined>(undefined);

    const fetchAssocs = useCallback(() => {
        if (!recordId) {
            setAssocs(null);
            return Promise.resolve();
        }
        const filter = JSON.stringify(
            getSearchObj({ field: "record_id", op: "eq", value: recordId })
        );
        const query = `query ContainerDashboards($filter: String) {
            container_dashboard_plain_list(filter: $filter, order: "+id") {
                id
                dashboard_id
                is_default
                dashboard_id_dashboard {
                    id
                    name
                    icon
                    value
                }
            }
        }`;
        return graphqlPost({ query, variables: { filter } })
            .then((response) => {
                const list: ContainerDashboardAssoc[] =
                    response?.data?.container_dashboard_plain_list ?? [];
                // Only associations whose dashboard still resolves are usable.
                setAssocs(list.filter((assoc) => assoc.dashboard_id_dashboard));
            })
            .catch(() => {
                setAssocs([]);
            });
    }, [recordId]);

    useEffect(() => {
        fetchAssocs();
    }, [fetchAssocs]);

    // Re-query when the Container settings dialog changes this container's
    // associations, so the tab strip updates live without a page refresh.
    useEffect(() => {
        if (!recordId) {
            return undefined;
        }
        return containerSettingsBus.onChanged((changedRecordId) => {
            if (changedRecordId === recordId) {
                fetchAssocs();
            }
        });
    }, [recordId, fetchAssocs]);

    // Switching containers resets the one-time default selection. Clearing assocs
    // avoids applying the previous container's default before the refetch lands.
    useEffect(() => {
        initedRef.current = undefined;
        setActiveTab(CONTAINER_DASHBOARD_DEFAULT_TAB);
        setAssocs(null);
    }, [recordId]);

    useEffect(() => {
        if (!assocs) {
            return;
        }
        // On the first load for this container, open its configured default
        // dashboard — the association marked is_default, else the built-in
        // "Default dashboard" tab (when no additional dashboard is the default).
        if (initedRef.current !== recordId) {
            initedRef.current = recordId;
            const defaultAssoc = assocs.find((assoc) => assoc.is_default);
            setActiveTab(
                defaultAssoc
                    ? defaultAssoc.dashboard_id_dashboard!.id
                    : CONTAINER_DASHBOARD_DEFAULT_TAB
            );
            return;
        }
        // Afterwards only keep the selection valid: if the active additional tab's
        // association disappears (removed via the settings dialog), fall back to
        // the built-in Default tab. A live default change does not yank the tab.
        setActiveTab((prev) => {
            if (prev === CONTAINER_DASHBOARD_DEFAULT_TAB) {
                return prev;
            }
            if (assocs.some((assoc) => assoc.dashboard_id_dashboard!.id === prev)) {
                return prev;
            }
            return CONTAINER_DASHBOARD_DEFAULT_TAB;
        });
    }, [assocs, recordId]);

    const hasExtraDashboards = (assocs?.length ?? 0) > 0;
    const isDefaultTab = !hasExtraDashboards || activeTab === CONTAINER_DASHBOARD_DEFAULT_TAB;
    const activeAssoc = assocs?.find(
        (assoc) => assoc.dashboard_id_dashboard!.id === activeTab
    );

    return {
        assocs,
        hasExtraDashboards,
        isDefaultTab,
        activeTab,
        setActiveTab,
        activeAssoc,
    };
}

export default useContainerDashboards;
